import useClipboard from "use-clipboard-hook";
import { useFacebookSDK } from "~hooks/useFacebookSDK";
import { showToast } from "~lib";
import { openExternalLink } from "~lib/helpers/openExternalLink";
import { useIsMobile } from "./useIsMobile";

interface BaseShareClickOptions {
  url: string;
}

interface EmailShareClickOptions extends BaseShareClickOptions {
  eventTitle: string;
}

interface FacebookShareClickOptions extends BaseShareClickOptions {
  variant: "share" | "direct-message";
  redirectUri?: string;
  hashtag?: string;
  onError?: (error: any) => void;
}

interface TwitterShareClickOptions extends BaseShareClickOptions {
  eventTitle: string;
  variant: "tweet" | "direct-message";
  hashtag?: string;
  onError?: (error: any) => void;
}

type CopyLinkClickOptions = BaseShareClickOptions;

export type ClickOptions =
  | FacebookShareClickOptions
  | EmailShareClickOptions
  | TwitterShareClickOptions
  | CopyLinkClickOptions;

type ShareTypeClickHandlers = {
  facebookHandler: (options: FacebookShareClickOptions) => void;
  twitterHandler: (options: TwitterShareClickOptions) => void;
  copyHandler: (options: CopyLinkClickOptions) => void;
  emailHandler: (options: EmailShareClickOptions) => void;
};

export const useSocialShare = (): ShareTypeClickHandlers => {
  useFacebookSDK();
  const { copy } = useClipboard();
  const isMobile = useIsMobile();

  const handleFacebookShareClick = ({
    url,
    hashtag,
    onError,
    redirectUri,
    variant,
  }: FacebookShareClickOptions) => {
    if (!window.FB) {
      const error = "Facebook SDK not loaded";
      onError?.(error);
      console.error(error);
      return;
    }

    const shareParams: fb.ShareDialogParams = {
      display: "popup",
      method: "share",
      href: url,
      redirect_uri: redirectUri,
      hashtag,
    };

    const sendParams: fb.SendDialogParams = {
      display: "popup",
      method: "send",
      link: url,
    };

    if (variant === "share") {
      window.FB.ui(shareParams, function (response) {
        if (response.error_message) {
          onError(response.error_message);
        }
      });
    } else {
      if (isMobile) {
        // Use the messenger app if on mobile (and cross fingers it's installed...)
        return openExternalLink({
          url: `fb-messenger://share?link=${encodeURIComponent(url)}`,
        });
      }

      // Otherwise use the send dialog
      window.FB.ui(sendParams, function (response) {
        if (response.error_message) {
          onError(response.error_message);
        }
      });
    }
  };

  const createMessageBody = (
    eventTitle: string,
    additionalMessage?: string
  ) => {
    return `${eventTitle} – Join me in registering for presale access!${
      additionalMessage ? ` ${additionalMessage}` : ""
    }`;
  };

  const handleTwitterShareClick = ({
    eventTitle,
    url,
    variant,
  }: TwitterShareClickOptions) => {
    const twitterAction =
      variant === "tweet" ? "/intent/tweet" : "/messages/compose";
    const tweetUrl = new URL(`https://twitter.com${twitterAction}`);

    // Used for direct message. The URL query param doesn't work for them,
    // so we add the URL directly in the message.
    const additionalMessage = variant === "direct-message" ? url : undefined;
    const params = new URLSearchParams({
      text: createMessageBody(eventTitle, additionalMessage),
      url,
    });

    const popupParams = `status=no,location=no,toolbar=no,menubar=no,
    width=0,height=0,left=-1000,top=-1000`;
    tweetUrl.search = params.toString();
    openExternalLink({
      url: tweetUrl.toString(),
      features: popupParams,
    });
  };

  const handleEmailShareClick = ({
    eventTitle,
    url,
  }: EmailShareClickOptions) => {
    // TODO add more info to the email body
    const body = `Hi, \n\nI'm inviting you to register for presale access to ${eventTitle}!\n\nRegister for presale access: ${url}`;
    openExternalLink({
      url: `mailto:?subject=${encodeURIComponent(
        createMessageBody(eventTitle)
      )}&body=${encodeURIComponent(body)}`,
    });
  };

  const handleCopyLinkClick = ({ url }: CopyLinkClickOptions) => {
    copy(url);
    showToast("Link copied to clipboard");
  };

  return {
    facebookHandler: handleFacebookShareClick,
    twitterHandler: handleTwitterShareClick,
    copyHandler: handleCopyLinkClick,
    emailHandler: handleEmailShareClick,
  };
};
